import React, { useEffect, useRef, useState, useMemo, useContext } from "react";
import {
  Dialog,
  Box,
  Typography,
  Stack,
  Button,
  Divider,
  Card,
  CardContent,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Checkbox,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  RadioGroup,
  Radio,
  Chip,
  CircularProgress,
} from "@mui/material";

import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import StoreIcon from "@mui/icons-material/Store";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import axios from "axios";
import { BASE_URL } from "../../config/api";
import { AuthContext } from "../../context/AuthContext";

/**
 * ClaimWizardDialog
 */
export default function ClaimWizardDialog({
  open,
  onClose,
  // resetForm,
  devices = [],
  addresses = [],
  channelPartners = [],
  retailerInfo = null,
  onSubmit,
  preselectedDeviceId = "",
  submitting = false,
}) {
  const STEPS = [
    "Select Product",
    "Issue Details",
    "Upload Photos",
    "Service Location",
    "Review & Submit",
  ];

   const { user } = useContext(AuthContext);
  /* ---------------- NORMALIZE DEVICES ---------------- */
  const normalizedDevices = useMemo(() => {
    return devices.map((d) => ({
      ...d,
      productId: d.product?.id,          // ✅ THIS IS THE KEY FIX
      displayName: d.product_name || "Device",
      displayPlan: `${d.brand_name || ""} ${d.model || ""}`.trim(),
      displayimei: d.imei1,
    }));
  }, [devices]);


  /* ---------------- STATE ---------------- */
  const [step, setStep] = useState(0);
  const [uploadingPhotos, setUploadingPhotos] = useState([]);
  const [draft, setDraft] = useState({
    device_id: "",
    issues: [],
    notes: "",
    photos: [],
    service: "pickup",
    pickup_address_id: "",
    drop_partner_id: "",
  });

  const [issues, setIssues] = useState([]);

  const [issuesLoading, setIssuesLoading] = useState(false);
  const [issuesError, setIssuesError] = useState("");

  const photoPreviews = useRef([]);
  const [previewPhoto, setPreviewPhoto] = useState(null);

  const [defaultRetailer, setDefaultRetailer] = useState(null);
  const [selectedRetailer, setSelectedRetailer] = useState(null);

  const [retailerPincode, setRetailerPincode] = useState("");
  const [retailerResults, setRetailerResults] = useState([]);
  const [retailerLoading, setRetailerLoading] = useState(false);

  const [errors, setErrors] = useState({});


  const activeRetailer = selectedRetailer || defaultRetailer;

  useEffect(() => {
    if (draft.service === "drop" && retailerInfo) {
      setDefaultRetailer(retailerInfo);
      setSelectedRetailer(null); // 👈 default is selected initially
    }
  }, [draft.service, retailerInfo]);

  const validateStep = (currentStep) => {
  const newErrors = {};

  // STEP 0 – Device
  if (currentStep === 0) {
    if (!draft.device_id) {
      newErrors.device = "Please select a device";
    }
  }

  // STEP 1 – Issues
  if (currentStep === 1) {
    if (draft.issues.length === 0) {
      newErrors.issues = "Please select at least one issue";
    }
  }

  // STEP 2 – Photos
  if (currentStep === 2) {
    if (draft.photos.length === 0) {
      newErrors.photos = "Please upload at least one photo";
    }

    const uploading = uploadingPhotos.some(
      (p) => p.status === "uploading"
    );

    if (uploading) {
      newErrors.photos = "Please wait for photo upload to finish";
    }
  }

  // STEP 3 – Service Location
  if (currentStep === 3) {
    if (draft.service === "pickup") {
      if (!draft.pickup_address_id) {
        newErrors.location = "Please select a pickup address";
      }
    }

    if (draft.service === "drop") {
      if (!activeRetailer?.id) {
        newErrors.location = "Please select a drop-off retailer";
      }
    }
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

  /* ---------------- INIT ---------------- */
  useEffect(() => {
    if (open) {
      setDraft({
        device_id: preselectedDeviceId || "",
        issues: [],
        notes: "",
        photos: [],
        service: "pickup",
        pickup_address_id: addresses?.[0]?.id || "",
        drop_partner_id: "",
      });
      setIssues([]);
      setStep(preselectedDeviceId ? 1 : 0);
    }
  }, [open, preselectedDeviceId, addresses]);

  useEffect(() => {
    return () => {
      try {
        photoPreviews.current.forEach((u) => URL.revokeObjectURL(u));
      } catch {}
    };
  }, []);

  /* ---------------- HELPERS ---------------- */
  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const handleNext = () => {
    if (!validateStep(step)) return;
    next();
  };

  const toggleIssue = (issue) => {
  setDraft((prev) => {
    const exists = prev.issues.find((i) => i.id === issue.id);

    return {
      ...prev,
      issues: exists
        ? prev.issues.filter((i) => i.id !== issue.id)
        : [...prev.issues, { id: issue.id, title: issue.title }],
    };
  });
};


  /* ---------------- FETCH ISSUES ---------------- */
  const fetchIssuesByProduct = async (productId) => {
  try {
    setIssues([]);
    setIssuesLoading(true);
    setIssuesError("");

    const res = await axios.get(
      `${BASE_URL}warranty/coverages/3`,
      {
        params: { product_id: productId },
      }
    );

    if (res.data?.status) {
      setIssues(res.data.data || []);
    } else {
      setIssues([]);
    }
  } catch (err) {
    setIssuesError("Failed to load issues");
    setIssues([]);
  } finally {
    setIssuesLoading(false);
  }
};

  /* ---------------- HANDLE DEVICE SELECT ---------------- */
  const handleSelectDevice = (device) => {
      if (!device.productId) {
        console.error("Product ID missing for device:", device);
        return;
      }

      setDraft((p) => ({
        ...p,
        device_id: device.id,
        issues: [],
      }));

      // ✅ EXACTLY FROM devices.product.id
      fetchIssuesByProduct(device.productId);
    };

 const uploadPhoto = async (file) => {
  const tempId = `${file.name}-${Date.now()}`;

  setUploadingPhotos((prev) => [
    ...prev,
    {
      tempId,
      name: file.name,
      status: "uploading",
      photo_id: null,
      url: null,
    },
  ]);

  try {
    // 🔥 COMPRESS IMAGE BEFORE UPLOAD
    const compressedFile = await compressImage(file, {
      maxWidth: 1280,
      maxHeight: 1280,
      quality: 0.7, // 🔥 tweak: 0.6–0.8
    });

    const formData = new FormData();
    formData.append("w_customer_id", user?.id);
    formData.append("photo_type", "front");
    formData.append("photo", compressedFile);

    const res = await axios.post(
      `${BASE_URL}warranty/claim/upload-photo`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );

    if (!res.data?.status) throw new Error("Upload failed");

    // ✅ Update UI list
    setUploadingPhotos((prev) =>
      prev.map((p) =>
        p.tempId === tempId
          ? {
              ...p,
              status: "success",
              photo_id: res.data.photo_id,
              url: res.data.photo_url,
            }
          : p
      )
    );

    // ✅ Store for claim submit
    setDraft((prev) => ({
      ...prev,
      photos: [
        ...prev.photos,
        {
          photo_id: res.data.photo_id,
          photo_url: res.data.photo_url,
        },
      ],
    }));

     console.log(
    "Original:",
    (file.size / 1024 / 1024).toFixed(2), "MB",
    "Compressed:",
    (compressedFile.size / 1024 / 1024).toFixed(2), "MB"
  );
  } catch (err) {
    console.error(err);
    setUploadingPhotos((prev) =>
      prev.map((p) =>
        p.tempId === tempId
          ? { ...p, status: "error" }
          : p
      )
    );
  }
};



  const handleAddPhotos = (files) => {
    const fileArr = Array.from(files || []);
    fileArr.forEach(uploadPhoto);
  };

  const fetchRetailersByPincode = async () => {
  if (!retailerPincode || retailerPincode.length !== 6) return;

  try {
    setRetailerLoading(true);

    const res = await axios.post(
      `${BASE_URL}warranty/retailers/by-pincode`,
      { pincode: retailerPincode }
    );

    if (res.data?.status) {
      setRetailerResults(res.data.data || []);
    } else {
      setRetailerResults([]);
    }
  } catch (err) {
    setRetailerResults([]);
  } finally {
    setRetailerLoading(false);
  }
};

  useEffect(() => {
    if (draft.service === "drop" && retailerInfo) {
      setDefaultRetailer(retailerInfo);
      setSelectedRetailer(null); // reset optional selection
    }
  }, [draft.service, retailerInfo]);

  const selectedDevice = normalizedDevices.find(
    (d) => d.id === draft.device_id
  );

  const pickupAddress = addresses.find(
    (a) => a.id === draft.pickup_address_id
  );

  const payload = {
    w_customer_id: user.id,
    w_device_id: draft.device_id,
    claim_type: draft.service, // pickup | drop

    issue_description: draft.issues.map(i => i.title).join(", "),
    coverage_ids: draft.issues.map(i => i.id),
    photo_ids: draft.photos.map(p => p.photo_id),

    ...(draft.service === "pickup" && {
      pickup_address_id: draft.pickup_address_id || 0,
    }),

    ...(draft.service === "drop" && {
      drop_retailer_id: activeRetailer?.id,
    }),
  };


  const raiseClaim = async () => {
    // 🔒 final full validation
    for (let i = 0; i <= 3; i++) {
      if (!validateStep(i)) {
        setStep(i);
        return;
      }
    }

    try {
      const res = await axios.post(
        `${BASE_URL}warranty/claim/raise`,
        payload
      );

      if (res.data?.status) {
        onSubmit?.();
      } else {
        throw new Error(res.data?.message || "Claim failed");
      }
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to raise claim");
    }
  };

  const compressImage = (
  file,
  {
    maxWidth = 1280,
    maxHeight = 1280,
    quality = 0.7,
  } = {}
) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      let { width, height } = img;

      // 🔹 Maintain aspect ratio
      if (width > maxWidth || height > maxHeight) {
        const scale = Math.min(
          maxWidth / width,
          maxHeight / height
        );
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);

          if (!blob) {
            reject(new Error("Image compression failed"));
            return;
          }

          const compressedFile = new File([blob], file.name, {
            type: "image/jpeg",
            lastModified: Date.now(),
          });

          resolve(compressedFile);
        },
        "image/jpeg",
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };

    img.src = url;
  });
};

const resetForm = () => {
    setStep(0);
    setIssues([]);
    setIssuesError("");
    setErrors({});
    setUploadingPhotos([]);
    setPreviewPhoto(null);
    setRetailerPincode("");
    setRetailerResults([]);
    setSelectedRetailer(null);
    setDefaultRetailer(null);

    setDraft({
      device_id: "",
      issues: [],
      notes: "",
      photos: [],
      service: "pickup",
      pickup_address_id: addresses?.[0]?.id || "",
      drop_partner_id: "",
    });
  };
 

  return (
    <Dialog open={open} onClose={!submitting ? onClose : null} fullWidth 
    sx={{
    "& .MuiDialog-paper": {
      margin: "8px",                 // 👈 reduce outer margin
      width: "calc(100% - 16px)",    // 👈 prevents overflow
    },
  }}>
      <Box p={3}>
        {/* HEADER */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight={900}>
              Raise a Claim
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Step {step + 1} of {STEPS.length}
            </Typography>
          </Box>
          <Button
            disabled={submitting}
            onClick={() => {
              resetForm();
              onClose();
            }}
          >
            Close
          </Button>

        </Stack>

        <Box mt={2}>
          <Stepper activeStep={step} alternativeLabel>
            {STEPS.map((s) => (
              <Step key={s}>
                <StepLabel>{s}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* ================= STEP 0 – SELECT DEVICE ================= */}
        {step === 0 && (
          <Stack spacing={1.5}>
            <Typography fontWeight={900}>Select Device</Typography>

            {normalizedDevices.map((d) => (
              <Card
                key={d.id}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  cursor: "pointer",
                  borderColor:
                    draft.device_id === d.id ? "#4f46e5" : undefined,
                }}
                onClick={() => handleSelectDevice(d)}
              >
                <CardContent sx={{ position: "relative" }}>
                  {/* ✅ Selected chip – top right */}
                  {draft.device_id === d.id && (
                    <Chip
                      size="small"
                      label="Selected"
                      color="primary"
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        fontWeight: 600,
                      }}
                    />
                  )}

                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar sx={{ bgcolor: "#eef2ff" }}>
                      <PhoneIphoneIcon color="primary" />
                    </Avatar>

                    <Box flex={1}>
                      <Typography fontWeight={900}>
                        {d.displayPlan}
                      </Typography>

                      <Typography variant="caption" color="text.secondary">
                        {d.displayName}
                      </Typography>

                      <Stack spacing={0.3} mt={0.5}>
                        {d.imei1 && (
                          <Typography variant="caption" color="text.secondary">
                            <b>IMEI 1:</b> {d.imei1}
                          </Typography>
                        )}

                        {d.imei2 && (
                          <Typography variant="caption" color="text.secondary">
                            <b>IMEI 2:</b> {d.imei2}
                          </Typography>
                        )}

                        {d.serial && (
                          <Typography variant="caption" color="text.secondary">
                            <b>Serial:</b> {d.serial}
                          </Typography>
                        )}

                        {d.available_claim && (
                          <Typography variant="caption" color="text.secondary">
                            <b>Claims:</b> {d.available_claim}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                </CardContent>

              </Card>
            ))}

            {errors.device && (
              <Typography color="error" variant="caption">
                {errors.device}
              </Typography>
            )}

          </Stack>
        )}

        {/* ================= STEP 1 – ISSUE DETAILS ================= */}
        {step === 1 && (
          <Stack spacing={1.5}>
            <Typography fontWeight={900}>Issue Details</Typography>

            {issuesLoading && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="caption">Loading issues…</Typography>
              </Stack>
            )}

            {issuesError && (
              <Typography variant="caption" color="error">
                {issuesError}
              </Typography>
            )}

            {!issuesLoading && issues.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No issues available for this product.
              </Typography>
            )}

            {!issuesLoading && issues.length > 0 && (
              <FormControl>
                <FormLabel>Select Issue(s)</FormLabel>
                <FormGroup>
                  {issues.map((issue) => (
                    <FormControlLabel
                      key={issue.id}
                      label={issue.title}
                      control={
                        <Checkbox
                          checked={draft.issues.some((i) => i.id === issue.id)}
                          onChange={() => toggleIssue(issue)}
                        />

                      }
                    />
                  ))}
                </FormGroup>
              </FormControl>
            )}

            {errors.issues && (
              <Typography color="error" variant="caption">
                {errors.issues}
              </Typography>
            )}


            <TextField
              label="Notes (optional)"
              multiline
              minRows={3}
              value={draft.notes}
              onChange={(e) =>
                setDraft((p) => ({ ...p, notes: e.target.value }))
              }
            />


          </Stack>
        )}

        {/* ================= STEP 2 – PHOTOS ================= */}
        {step === 2 && (
  <Stack spacing={2}>
    <Typography fontWeight={900}>Upload Photos</Typography>

    {/* ===== DROP ZONE ===== */}
    <Box
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        handleAddPhotos(e.dataTransfer.files);
      }}
      sx={{
        border: "2px dashed #e5e7eb",
        borderRadius: 2,
        p: 4,
        textAlign: "center",
        bgcolor: "#f9fafb",
      }}
    >
      {/* Upload Icon */}
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          border: "2px solid #3b82f6",
          mx: "auto",
          mb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#3b82f6",
          fontSize: 20,
        }}
      >
        ⬆
      </Box>

      <Typography fontWeight={600}>Drag your file here</Typography>
      <Typography variant="caption" color="text.secondary">
        or
      </Typography>

      <Button
        component="label"
        sx={{
          mt: 1,
          px: 4,
          borderRadius: "999px",
          bgcolor: "#2196f3",
          color: "#fff",
          textTransform: "none",
          "&:hover": { bgcolor: "#1e88e5" },
        }}
      >
        Browse
        <input
          hidden
          multiple
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => handleAddPhotos(e.target.files)}
        />
      </Button>
    </Box>

    {/* ===== FILE LIST CARD ===== */}
    {uploadingPhotos.length > 0 && (
      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: 2,
          boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
          p: 2,
        }}
      >
        <Stack spacing={2}>
          {uploadingPhotos.map((p) => (
            <Box key={p.tempId}>
              {/* File row */}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography fontSize={14}>{p.name}</Typography>

                {/* Right icon (visual only) */}
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    border: "1px solid #e5e7eb",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    color: "#9ca3af",
                  }}
                >
                  ✕
                </Box>
              </Stack>

              {/* Progress bar */}
              <Box
                sx={{
                  mt: 0.8,
                  height: 5,
                  borderRadius: 3,
                  bgcolor: "#e5e7eb",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: `${p.progress || 100}%`,
                    height: "100%",
                    bgcolor:
                      p.status === "success"
                        ? "#22c55e"
                        : p.status === "error"
                        ? "#ef4444"
                        : "#facc15",
                  }}
                />
              </Box>

              {/* Status */}
              <Typography
                variant="caption"
                sx={{ color: "#9ca3af", mt: 0.3 }}
              >
                {p.status === "uploading" && "Uploading"}
                {p.status === "success" && "Completed"}
                {p.status === "error" && "Failed"}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    )}

    {errors.photos && (
      <Typography color="error" variant="caption">
        {errors.photos}
      </Typography>
    )}
  </Stack>
)}


        {/* ================= STEP 3 – LOCATION ================= */}
        {step === 3 && (
          <Stack spacing={2}>
            <Typography fontWeight={900}>Service Method</Typography>

            <RadioGroup
              row
              value={draft.service}
              onChange={(e) =>
                setDraft((p) => ({ ...p, service: e.target.value }))
              }
            >
              <FormControlLabel value="pickup" control={<Radio />} label="Pickup" />
              <FormControlLabel value="drop" control={<Radio />} label="Drop-off" />
            </RadioGroup>

            {/* ================= PICKUP ================= */}
            {draft.service === "pickup" &&
              addresses.map((a) => (
                <Card
                  key={a.id}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    cursor: "pointer",
                    borderColor:
                      draft.pickup_address_id === a.id ? "#4f46e5" : undefined,
                  }}
                  onClick={() =>
                    setDraft((p) => ({ ...p, pickup_address_id: a.id }))
                  }
                >
                  <CardContent>
                    <Typography fontWeight={900}>{a.name}</Typography>
                    <Typography variant="caption">
                      {[a.address1, a.city, a.state, a.pincode]
                        .filter(Boolean)
                        .join(", ")}
                    </Typography>
                  </CardContent>
                </Card>
              ))}

            {/* ================= DROP-OFF ================= */}
            {draft.service === "drop" && (
              <>
                {/* DEFAULT RETAILER */}
                {defaultRetailer && (
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      cursor: "pointer",
                      borderColor:
                        activeRetailer?.id === defaultRetailer.id
                          ? "#4f46e5"
                          : "#e5e7eb",
                      backgroundColor:
                        activeRetailer?.id === defaultRetailer.id
                          ? "#eef2ff"
                          : "transparent",
                    }}
                    onClick={() => {
                      setSelectedRetailer(null); // 👈 THIS IS THE KEY LINE
                    }}
                  >
                    <CardContent>
                      <Typography fontWeight={900}>
                        {defaultRetailer.business_name}
                      </Typography>

                      <Typography variant="caption">
                        {[
                          defaultRetailer.address_line1,
                          defaultRetailer.city,
                          defaultRetailer.state,
                          defaultRetailer.pincode,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </Typography>

                      {activeRetailer?.id === defaultRetailer.id && (
                        <Chip
                          size="small"
                          color="primary"
                          label="Selected"
                          sx={{ mt: 1 }}
                        />
                      )}
                    </CardContent>
                  </Card>
                )}

                <Divider />

                {/* SEARCH BY PINCODE */}
                <Typography fontWeight={700}>
                  Select another retailer
                </Typography>

                <Stack direction="row" spacing={1}>
                  <TextField
                    label="Enter Pincode"
                    value={retailerPincode}
                    onChange={(e) => setRetailerPincode(e.target.value)}
                    inputProps={{ maxLength: 6 }}
                    size="small"
                  />
                  <Button
                    variant="outlined"
                    onClick={fetchRetailersByPincode}
                    disabled={retailerPincode.length !== 6 || retailerLoading}
                  >
                    Search
                  </Button>
                </Stack>

                {retailerLoading && <CircularProgress size={18} />}

                {retailerResults.map((r) => (
                  <Card
                    key={r.id}
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      cursor: "pointer",
                      borderColor:
                        activeRetailer?.id === r.id ? "#16a34a" : "#e5e7eb",
                      backgroundColor:
                        activeRetailer?.id === r.id ? "#ecfdf5" : "transparent",
                    }}
                    onClick={() => setSelectedRetailer(r)}
                  >
                    <CardContent>
                      <Typography fontWeight={900}>{r.business_name}</Typography>
                      <Typography variant="caption">
                        {[r.address_line1, r.city, r.state, r.pincode]
                          .filter(Boolean)
                          .join(", ")}
                      </Typography>

                      {activeRetailer?.id === r.id && (
                        <Chip
                          size="small"
                          color="success"
                          label="Selected"
                          sx={{ mt: 1 }}
                        />
                      )}
                    </CardContent>
                  </Card>
                ))}
              </>
            )}

            {errors.location && (
              <Typography color="error" variant="caption">
                {errors.location}
              </Typography>
            )}

          </Stack>
        )}

        {/* ================= STEP 4 – REVIEW ================= */}

        {step === 4 && (
          <Stack spacing={2}>
            <Typography fontWeight={900}>Review Your Claim</Typography>

            {/* DEVICE */}
            <Card variant="outlined">
              <CardContent>
                <Typography fontWeight={700}>Device</Typography>
                <Typography variant="body2">
                  {selectedDevice?.displayPlan}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedDevice?.displayName}
                </Typography>
              </CardContent>
            </Card>

            {/* ISSUES */}
            <Card variant="outlined">
              <CardContent>
                <Typography fontWeight={700}>Issues</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {draft.issues.map((i) => (
                    <Chip key={i.id} label={i.title} size="small" />
                  ))}
                </Stack>


                {draft.notes && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body2">{draft.notes}</Typography>
                  </>
                )}
              </CardContent>
            </Card>

            {/* SERVICE METHOD */}
            <Card variant="outlined">
              <CardContent>
                <Typography fontWeight={700}>Service Method</Typography>
                <Typography variant="body2">
                  {draft.service === "pickup" ? "Pickup from Address" : "Drop-off at Retailer"}
                </Typography>

                <Divider sx={{ my: 1 }} />

                {/* PICKUP ADDRESS */}
                {draft.service === "pickup" && pickupAddress && (
                  <>
                    <Typography fontWeight={600}>Pickup Address</Typography>
                    <Typography variant="caption">
                      {[pickupAddress.address1,
                        pickupAddress.city,
                        pickupAddress.state,
                        pickupAddress.pincode]
                        .filter(Boolean)
                        .join(", ")}
                    </Typography>
                  </>
                )}

                {/* DROP RETAILER */}
                {draft.service === "drop" && activeRetailer && (
                  <>
                    <Typography fontWeight={600}>Drop-off Retailer</Typography>
                    <Typography variant="caption">
                      {[activeRetailer.address_line1,
                        activeRetailer.city,
                        activeRetailer.state,
                        activeRetailer.pincode]
                        .filter(Boolean)
                        .join(", ")}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>

            {/* PHOTOS */}
            <Card variant="outlined">
              <CardContent>
                <Typography fontWeight={700}>
                  Uploaded Photos ({draft.photos.length})
                </Typography>

                {draft.photos.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    No photos uploaded
                  </Typography>
                ) : (
                  <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
                    {draft.photos.map((p, i) => (
                      <Box
                        key={i}
                        component="img"
                        src={p.photo_url}
                        alt={`photo-${i}`}
                        sx={{
                          width: 70,
                          height: 70,
                          objectFit: "cover",
                          borderRadius: 1,
                          border: "1px solid #e5e7eb",
                          cursor: "pointer",
                        }}
                        onClick={() => setPreviewPhoto(p.photo_url)}
                      />
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Stack>
        )}


        <Divider sx={{ my: 2 }} />

        {/* ACTIONS */}
        <Stack direction="row" spacing={1}>
          <Button
            fullWidth
            variant="outlined"
            disabled={step === 0 || submitting}
            onClick={back}
          >
            Back
          </Button>

          {step < 4 ? (
            <Button
              fullWidth
              variant="contained"
              disabled={
                (step === 0 && !draft.device_id) ||
                (step === 1 && draft.issues.length === 0)
              }
              onClick={handleNext}
            >
              Next
            </Button>
          ) : (
            <Button
              fullWidth
              variant="contained"
              disabled={submitting}
              onClick={raiseClaim}
            >
              Submit Claim
            </Button>
          )}
        </Stack>
      </Box>

      {/* PHOTO PREVIEW DIALOG */}
      <Dialog
        open={Boolean(previewPhoto)}
        onClose={() => setPreviewPhoto(null)}
        maxWidth="sm"
        fullWidth
      >
        <Box p={2}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography fontWeight={700}>Photo Preview</Typography>
            <Button onClick={() => setPreviewPhoto(null)}>Close</Button>
          </Stack>

          {previewPhoto && (
            <Box
              component="img"
              src={previewPhoto}
              alt="Uploaded Preview"
              sx={{
                width: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
                borderRadius: 1,
                border: "1px solid #e5e7eb",
              }}
            />
          )}
        </Box>
      </Dialog>

    </Dialog>
  );
}



// import React, { useEffect, useRef, useState, useMemo } from "react";
// import {
//   Dialog,
//   Box,
//   Typography,
//   Stack,
//   Button,
//   Divider,
//   Card,
//   CardContent,
//   Avatar,
//   Stepper,
//   Step,
//   StepLabel,
//   TextField,
//   Checkbox,
//   FormControl,
//   FormLabel,
//   FormGroup,
//   FormControlLabel,
//   RadioGroup,
//   Radio,
//   Chip,
// } from "@mui/material";

// import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
// import StoreIcon from "@mui/icons-material/Store";
// import OpenInNewIcon from "@mui/icons-material/OpenInNew";

// /**
//  * ClaimWizardDialog
//  *
//  * Props:
//  * - open
//  * - onClose
//  * - devices []  (API devices)
//  * - addresses []
//  * - channelPartners []
//  * - onSubmit (claimPayload)
//  * - preselectedDeviceId (optional)
//  * - submitting (boolean)
//  */
// export default function ClaimWizardDialog({
//   open,
//   onClose,
//   devices = [],
//   addresses = [],
//   channelPartners = [],
//   onSubmit,
//   preselectedDeviceId = "",
//   submitting = false,
// }) {
//   const STEPS = [
//     "Select Product",
//     "Issue Details",
//     "Upload Photos",
//     "Service Location",
//     "Review & Submit",
//   ];

//   const ISSUE_OPTIONS = [
//     "Screen Damage",
//     "Liquid Damage",
//     "Battery Issue",
//     "Speaker / Mic Issue",
//     "Charging Port Issue",
//     "Power / No Display",
//     "Other",
//   ];

//   /* ------------------------------------------------------------
//      🔥 NORMALIZE DEVICES FOR UI
//      ------------------------------------------------------------ */
//   const normalizedDevices = useMemo(() => {
//     return devices.map((d) => ({
//       ...d,
//       displayName: d.product_name || "Device",
//       displayPlan: `${d.brand_name || ""} ${d.model || ""}`.trim(),
//     }));
//   }, [devices]);

//   const [step, setStep] = useState(0);
//   const [draft, setDraft] = useState({
//     device_id: "",
//     issues: [],
//     notes: "",
//     photos: [],
//     service: "pickup",
//     pickup_address_id: "",
//     drop_partner_id: "",
//   });

//   const photoPreviews = useRef([]);

//   /* ------------------------------------------------------------
//      INIT / RESET WHEN OPEN
//      ------------------------------------------------------------ */
//   useEffect(() => {
//     if (open) {
//       setDraft({
//         device_id: preselectedDeviceId || "",
//         issues: [],
//         notes: "",
//         photos: [],
//         service: "pickup",
//         pickup_address_id: addresses?.[0]?.id || "",
//         drop_partner_id: "",
//       });
//       setStep(preselectedDeviceId ? 1 : 0);
//     }
//   }, [open, preselectedDeviceId, addresses]);

//   useEffect(() => {
//     return () => {
//       try {
//         photoPreviews.current.forEach((u) => URL.revokeObjectURL(u));
//       } catch {}
//     };
//   }, []);

//   const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
//   const back = () => setStep((s) => Math.max(0, s - 1));

//   const toggleIssue = (issue) => {
//     setDraft((p) => ({
//       ...p,
//       issues: p.issues.includes(issue)
//         ? p.issues.filter((i) => i !== issue)
//         : [...p.issues, issue],
//     }));
//   };

//   const handleAddPhotos = (files) => {
//     const arr = Array.from(files || []);
//     const urls = arr.map((f) => URL.createObjectURL(f));
//     photoPreviews.current.push(...urls);

//     setDraft((p) => ({
//       ...p,
//       photos: [...p.photos, ...arr],
//     }));
//   };

//   const submit = () => {
//     onSubmit?.({
//       ...draft,
//       photos_count: draft.photos.length,
//     });
//   };

//   return (
//     <Dialog open={open} onClose={!submitting ? onClose : null} fullWidth>
//       <Box p={3}>
//         {/* HEADER */}
//         <Stack direction="row" justifyContent="space-between" alignItems="center">
//           <Box>
//             <Typography variant="h6" fontWeight={900}>
//               Raise a Claim
//             </Typography>
//             <Typography variant="body2" color="text.secondary">
//               Step {step + 1} of {STEPS.length}
//             </Typography>
//           </Box>

//           <Button disabled={submitting} onClick={onClose}>
//             Close
//           </Button>
//         </Stack>

//         <Box mt={2}>
//           <Stepper activeStep={step} alternativeLabel>
//             {STEPS.map((s) => (
//               <Step key={s}>
//                 <StepLabel>{s}</StepLabel>
//               </Step>
//             ))}
//           </Stepper>
//         </Box>

//         <Divider sx={{ my: 2 }} />

//         {/* ================= STEP 0 – SELECT DEVICE ================= */}
//         {step === 0 && (
//           <Stack spacing={1.5}>
//             <Typography fontWeight={900}>Select Device</Typography>

//             {normalizedDevices.length === 0 && (
//               <Typography variant="body2" color="text.secondary">
//                 No devices found.
//               </Typography>
//             )}

//             {normalizedDevices.map((d) => (
//               <Card
//                 key={d.id}
//                 variant="outlined"
//                 sx={{
//                   borderRadius: 2,
//                   cursor: "pointer",
//                   borderColor:
//                     draft.device_id === d.id ? "#4f46e5" : undefined,
//                 }}
//                 onClick={() =>
//                   setDraft((p) => ({ ...p, device_id: d.id }))
//                 }
//               >
//                 <CardContent>
//                   <Stack direction="row" spacing={1.5} alignItems="center">
//                     <Avatar sx={{ bgcolor: "#eef2ff" }}>
//                       <PhoneIphoneIcon color="primary" />
//                     </Avatar>

//                     <Box flex={1}>
//                       <Typography fontWeight={900}>
//                         {d.displayPlan || "—"}
//                       </Typography>
//                       <Typography variant="caption" color="text.secondary">
//                         {d.displayName} 
//                       </Typography>
//                     </Box>

//                     {draft.device_id === d.id && (
//                       <Chip size="small" label="Selected" color="primary" />
//                     )}
//                   </Stack>
//                 </CardContent>
//               </Card>
//             ))}
//           </Stack>
//         )}

//         {/* ================= STEP 1 – ISSUES ================= */}
//         {step === 1 && (
//           <Stack spacing={1.5}>
//             <Typography fontWeight={900}>Issue Details</Typography>

//             <FormControl>
//               <FormLabel>Select Issue(s)</FormLabel>
//               <FormGroup>
//                 {ISSUE_OPTIONS.map((opt) => (
//                   <FormControlLabel
//                     key={opt}
//                     label={opt}
//                     control={
//                       <Checkbox
//                         checked={draft.issues.includes(opt)}
//                         onChange={() => toggleIssue(opt)}
//                       />
//                     }
//                   />
//                 ))}
//               </FormGroup>
//             </FormControl>

//             <TextField
//               label="Notes (optional)"
//               multiline
//               minRows={3}
//               value={draft.notes}
//               onChange={(e) =>
//                 setDraft((p) => ({ ...p, notes: e.target.value }))
//               }
//             />
//           </Stack>
//         )}

//         {/* ================= STEP 2 – PHOTOS ================= */}
//         {step === 2 && (
//           <Stack spacing={1.5}>
//             <Typography fontWeight={900}>Upload Photos</Typography>

//             <Button component="label" variant="outlined">
//               Add Photos
//               <input
//                 hidden
//                 multiple
//                 type="file"
//                 accept="image/*"
//                 onChange={(e) => handleAddPhotos(e.target.files)}
//               />
//             </Button>

//             {draft.photos.map((f, i) => (
//               <Typography key={i} variant="caption">
//                 {f.name}
//               </Typography>
//             ))}
//           </Stack>
//         )}

//         {/* ================= STEP 3 – LOCATION ================= */}
//         {step === 3 && (
//           <Stack spacing={1.5}>
//             <Typography fontWeight={900}>Service Method</Typography>

//             <RadioGroup
//               row
//               value={draft.service}
//               onChange={(e) =>
//                 setDraft((p) => ({ ...p, service: e.target.value }))
//               }
//             >
//               <FormControlLabel
//                 value="pickup"
//                 control={<Radio />}
//                 label="Pickup"
//               />
//               <FormControlLabel
//                 value="drop"
//                 control={<Radio />}
//                 label="Drop-off"
//               />
//             </RadioGroup>

//             {draft.service === "pickup" &&
//               addresses.map((a) => (
//                 <Card
//                   key={a.id}
//                   variant="outlined"
//                   sx={{
//                     borderRadius: 2,
//                     cursor: "pointer",
//                     borderColor:
//                       draft.pickup_address_id === a.id
//                         ? "#4f46e5"
//                         : undefined,
//                   }}
//                   onClick={() =>
//                     setDraft((p) => ({
//                       ...p,
//                       pickup_address_id: a.id,
//                     }))
//                   }
//                 >
//                   <CardContent>
//                     <Typography fontWeight={900}>{a.name}</Typography>
//                     <Typography variant="caption">
//                       {[a.address1, a.city, a.state, a.pincode]
//                         .filter(Boolean)
//                         .join(", ")}
//                     </Typography>
//                   </CardContent>
//                 </Card>
//               ))}
//           </Stack>
//         )}

//         {/* ================= STEP 4 – REVIEW ================= */}
//         {step === 4 && (
//           <Stack spacing={1.2}>
//             <Typography fontWeight={900}>Review</Typography>
//             <Typography variant="body2">
//               <b>Issues:</b> {draft.issues.join(", ")}
//             </Typography>
//             <Typography variant="body2">
//               <b>Service:</b>{" "}
//               {draft.service === "pickup" ? "Pickup" : "Drop-off"}
//             </Typography>
//             <Typography variant="body2">
//               <b>Photos:</b> {draft.photos.length}
//             </Typography>
//           </Stack>
//         )}

//         <Divider sx={{ my: 2 }} />

//         {/* ACTIONS */}
//         <Stack direction="row" spacing={1}>
//           <Button
//             fullWidth
//             variant="outlined"
//             disabled={step === 0 || submitting}
//             onClick={back}
//           >
//             Back
//           </Button>

//           {step < 4 ? (
//             <Button
//               fullWidth
//               variant="contained"
//               disabled={
//                 (step === 0 && !draft.device_id) ||
//                 (step === 1 && draft.issues.length === 0)
//               }
//               onClick={next}
//             >
//               Next
//             </Button>
//           ) : (
//             <Button
//               fullWidth
//               variant="contained"
//               disabled={submitting}
//               onClick={submit}
//             >
//               {submitting ? "Submitting..." : "Submit Claim"}
//             </Button>
//           )}
//         </Stack>
//       </Box>
//     </Dialog>
//   );
// }



// // src/dialogs/ClaimWizardDialog.jsx
// import React, { useEffect, useRef, useState } from "react";
// import {
//   Dialog,
//   Box,
//   Typography,
//   Stack,
//   Button,
//   Divider,
//   Card,
//   CardContent,
//   Avatar,
//   Stepper,
//   Step,
//   StepLabel,
//   TextField,
//   Checkbox,
//   FormControl,
//   FormLabel,
//   FormGroup,
//   FormControlLabel,
//   RadioGroup,
//   Radio,
//   Chip,
// } from "@mui/material";

// import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
// import StoreIcon from "@mui/icons-material/Store";
// import OpenInNewIcon from "@mui/icons-material/OpenInNew";

// /**
//  * ClaimWizardDialog
//  *
//  * Props:
//  * - open
//  * - onClose
//  * - devices []
//  * - addresses []
//  * - channelPartners []
//  * - onSubmit (claimPayload)
//  * - preselectedDeviceId (optional)
//  * - submitting (boolean)
//  */
// export default function ClaimWizardDialog({
//   open,
//   onClose,
//   devices = [],
//   addresses = [],
//   channelPartners = [],
//   onSubmit,
//   preselectedDeviceId = "",
//   submitting = false,
// }) {
//   const STEPS = [
//     "Select Product",
//     "Issue Details",
//     "Upload Photos",
//     "Service Location",
//     "Review & Submit",
//   ];

//   const ISSUE_OPTIONS = [
//     "Screen Damage",
//     "Liquid Damage",
//     "Battery Issue",
//     "Speaker / Mic Issue",
//     "Charging Port Issue",
//     "Power / No Display",
//     "Other",
//   ];

//   const [step, setStep] = useState(0);
//   const [draft, setDraft] = useState({
//     device_id: "",
//     issues: [],
//     notes: "",
//     photos: [],
//     service: "pickup",
//     pickup_address_id: "",
//     drop_partner_id: "",
//   });

//   const photoPreviews = useRef([]);

//   useEffect(() => {
//     if (open) {
//       setDraft({
//         device_id: preselectedDeviceId || "",
//         issues: [],
//         notes: "",
//         photos: [],
//         service: "pickup",
//         pickup_address_id: addresses?.[0]?.id || "",
//         drop_partner_id: "",
//       });
//       setStep(preselectedDeviceId ? 1 : 0);
//     }
//   }, [open, preselectedDeviceId, addresses]);

//   useEffect(() => {
//     return () => {
//       try {
//         photoPreviews.current.forEach((u) => URL.revokeObjectURL(u));
//       } catch {}
//     };
//   }, []);

//   const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
//   const back = () => setStep((s) => Math.max(0, s - 1));

//   const toggleIssue = (issue) => {
//     setDraft((p) => ({
//       ...p,
//       issues: p.issues.includes(issue)
//         ? p.issues.filter((i) => i !== issue)
//         : [...p.issues, issue],
//     }));
//   };

//   const handleAddPhotos = (files) => {
//     const arr = Array.from(files || []);
//     const urls = arr.map((f) => URL.createObjectURL(f));
//     photoPreviews.current.push(...urls);

//     setDraft((p) => ({
//       ...p,
//       photos: [...p.photos, ...arr],
//     }));
//   };

//   const submit = () => {
//     onSubmit?.({
//       ...draft,
//       photos_count: draft.photos.length,
//     });
//   };

//   return (
//     <Dialog open={open} onClose={!submitting ? onClose : null} fullWidth>
//       <Box p={3}>
//         {/* HEADER */}
//         <Stack direction="row" justifyContent="space-between" alignItems="center">
//           <Box>
//             <Typography variant="h6" fontWeight={900}>
//               Raise a Claim
//             </Typography>
//             <Typography variant="body2" color="text.secondary">
//               Step {step + 1} of {STEPS.length}
//             </Typography>
//           </Box>

//           <Button disabled={submitting} onClick={onClose}>
//             Close
//           </Button>
//         </Stack>

//         <Box mt={2}>
//           <Stepper activeStep={step} alternativeLabel>
//             {STEPS.map((s) => (
//               <Step key={s}>
//                 <StepLabel>{s}</StepLabel>
//               </Step>
//             ))}
//           </Stepper>
//         </Box>

//         <Divider sx={{ my: 2 }} />

//         {/* STEP 0 – DEVICE */}
//         {step === 0 && (
//           <Stack spacing={1.5}>
//             <Typography fontWeight={900}>Select Device</Typography>
//             {devices.map((d) => (
//               <Card
//                 key={d.id}
//                 variant="outlined"
//                 sx={{
//                   borderRadius: 2,
//                   cursor: "pointer",
//                   borderColor:
//                     draft.device_id === d.id ? "#4f46e5" : undefined,
//                 }}
//                 onClick={() =>
//                   setDraft((p) => ({ ...p, device_id: d.id }))
//                 }
//               >
//                 <CardContent>
//                   <Stack direction="row" spacing={1.5} alignItems="center">
//                     <Avatar sx={{ bgcolor: "#eef2ff" }}>
//                       <PhoneIphoneIcon color="primary" />
//                     </Avatar>
//                     <Box flex={1}>
//                       <Typography fontWeight={900}>{d.name}</Typography>
//                       <Typography variant="caption" color="text.secondary">
//                         {d.plan}
//                       </Typography>
//                     </Box>
//                     {draft.device_id === d.id && (
//                       <Chip size="small" label="Selected" color="primary" />
//                     )}
//                   </Stack>
//                 </CardContent>
//               </Card>
//             ))}
//           </Stack>
//         )}

//         {/* STEP 1 – ISSUES */}
//         {step === 1 && (
//           <Stack spacing={1.5}>
//             <Typography fontWeight={900}>Issue Details</Typography>

//             <FormControl>
//               <FormLabel>Select Issue(s)</FormLabel>
//               <FormGroup>
//                 {ISSUE_OPTIONS.map((opt) => (
//                   <FormControlLabel
//                     key={opt}
//                     label={opt}
//                     control={
//                       <Checkbox
//                         checked={draft.issues.includes(opt)}
//                         onChange={() => toggleIssue(opt)}
//                       />
//                     }
//                   />
//                 ))}
//               </FormGroup>
//             </FormControl>

//             <TextField
//               label="Notes (optional)"
//               multiline
//               minRows={3}
//               value={draft.notes}
//               onChange={(e) =>
//                 setDraft((p) => ({ ...p, notes: e.target.value }))
//               }
//             />
//           </Stack>
//         )}

//         {/* STEP 2 – PHOTOS */}
//         {step === 2 && (
//           <Stack spacing={1.5}>
//             <Typography fontWeight={900}>Upload Photos</Typography>

//             <Button component="label" variant="outlined">
//               Add Photos
//               <input
//                 hidden
//                 multiple
//                 type="file"
//                 accept="image/*"
//                 onChange={(e) => handleAddPhotos(e.target.files)}
//               />
//             </Button>

//             {draft.photos.map((f, i) => (
//               <Typography key={i} variant="caption">
//                 {f.name}
//               </Typography>
//             ))}
//           </Stack>
//         )}

//         {/* STEP 3 – LOCATION */}
//         {step === 3 && (
//           <Stack spacing={1.5}>
//             <Typography fontWeight={900}>Service Method</Typography>

//             <RadioGroup
//               row
//               value={draft.service}
//               onChange={(e) =>
//                 setDraft((p) => ({
//                   ...p,
//                   service: e.target.value,
//                 }))
//               }
//             >
//               <FormControlLabel
//                 value="pickup"
//                 control={<Radio />}
//                 label="Pickup"
//               />
//               <FormControlLabel
//                 value="drop"
//                 control={<Radio />}
//                 label="Drop-off"
//               />
//             </RadioGroup>

//             {draft.service === "pickup" &&
//               addresses.map((a) => (
//                 <Card
//                   key={a.id}
//                   variant="outlined"
//                   sx={{
//                     borderRadius: 2,
//                     cursor: "pointer",
//                     borderColor:
//                       draft.pickup_address_id === a.id
//                         ? "#4f46e5"
//                         : undefined,
//                   }}
//                   onClick={() =>
//                     setDraft((p) => ({
//                       ...p,
//                       pickup_address_id: a.id,
//                     }))
//                   }
//                 >
//                   <CardContent>
//                     <Typography fontWeight={900}>{a.name}</Typography>
//                     <Typography variant="caption">
//                       {[a.address1, a.city, a.state, a.pincode]
//                         .filter(Boolean)
//                         .join(", ")}
//                     </Typography>
//                   </CardContent>
//                 </Card>
//               ))}

//             {draft.service === "drop" &&
//               channelPartners.map((cp) => (
//                 <Card
//                   key={cp.id}
//                   variant="outlined"
//                   sx={{
//                     borderRadius: 2,
//                     cursor: "pointer",
//                     borderColor:
//                       draft.drop_partner_id === cp.id
//                         ? "#4f46e5"
//                         : undefined,
//                   }}
//                   onClick={() =>
//                     setDraft((p) => ({
//                       ...p,
//                       drop_partner_id: cp.id,
//                     }))
//                   }
//                 >
//                   <CardContent>
//                     <Stack direction="row" spacing={1.5}>
//                       <Avatar sx={{ bgcolor: "#eef2ff" }}>
//                         <StoreIcon color="primary" />
//                       </Avatar>
//                       <Box flex={1}>
//                         <Typography fontWeight={900}>{cp.name}</Typography>
//                         <Typography variant="caption">
//                           {cp.address}
//                         </Typography>
//                         <Button
//                           size="small"
//                           endIcon={<OpenInNewIcon />}
//                           onClick={(e) => {
//                             e.stopPropagation();
//                             window.open(
//                               `https://www.google.com/maps/search/?api=1&query=${cp.lat},${cp.lng}`,
//                               "_blank"
//                             );
//                           }}
//                         >
//                           Maps
//                         </Button>
//                       </Box>
//                     </Stack>
//                   </CardContent>
//                 </Card>
//               ))}
//           </Stack>
//         )}

//         {/* STEP 4 – REVIEW */}
//         {step === 4 && (
//           <Stack spacing={1.2}>
//             <Typography fontWeight={900}>Review</Typography>
//             <Typography variant="body2">
//               <b>Issues:</b> {draft.issues.join(", ")}
//             </Typography>
//             <Typography variant="body2">
//               <b>Service:</b>{" "}
//               {draft.service === "pickup" ? "Pickup" : "Drop-off"}
//             </Typography>
//             <Typography variant="body2">
//               <b>Photos:</b> {draft.photos.length}
//             </Typography>
//           </Stack>
//         )}

//         <Divider sx={{ my: 2 }} />

//         {/* ACTIONS */}
//         <Stack direction="row" spacing={1}>
//           <Button
//             fullWidth
//             variant="outlined"
//             disabled={step === 0 || submitting}
//             onClick={back}
//           >
//             Back
//           </Button>

//           {step < 4 ? (
//             <Button
//               fullWidth
//               variant="contained"
//               disabled={
//                 (step === 0 && !draft.device_id) ||
//                 (step === 1 && draft.issues.length === 0)
//               }
//               onClick={next}
//             >
//               Next
//             </Button>
//           ) : (
//             <Button
//               fullWidth
//               variant="contained"
//               disabled={submitting}
//               onClick={submit}
//             >
//               {submitting ? "Submitting..." : "Submit Claim"}
//             </Button>
//           )}
//         </Stack>
//       </Box>
//     </Dialog>
//   );
// }
